import { useEffect, useRef, useState, useCallback } from 'react'

interface ModalProps {
  isOpen: boolean
  onClose: () => void
  title: string
  children: React.ReactNode
  footer?: React.ReactNode
  size?: 'sm' | 'md' | 'lg'
  transparent?: boolean // No backdrop blur, draggable
}

export default function Modal({ isOpen, onClose, title, children, footer, size = 'md', transparent }: ModalProps) {
  const [offset, setOffset] = useState({ x: 0, y: 0 })
  const dragRef = useRef<{ startX: number; startY: number; offsetX: number; offsetY: number } | null>(null)
  const modalRef = useRef<HTMLDivElement>(null)

  // Reset position when opening
  useEffect(() => {
    if (isOpen) setOffset({ x: 0, y: 0 })
  }, [isOpen])

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose()
      }
    }
    window.addEventListener('keydown', handleEscape)
    return () => window.removeEventListener('keydown', handleEscape)
  }, [isOpen, onClose])

  // Drag handlers (transparent mode only)
  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    if (!transparent) return
    // Only drag from the header bar
    const target = e.target as HTMLElement
    if (!target.closest('[data-drag-handle]')) return

    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      offsetX: offset.x,
      offsetY: offset.y,
    }
    ;(e.target as HTMLElement).setPointerCapture(e.pointerId)
  }, [transparent, offset])

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!dragRef.current) return
    setOffset({
      x: dragRef.current.offsetX + (e.clientX - dragRef.current.startX),
      y: dragRef.current.offsetY + (e.clientY - dragRef.current.startY),
    })
  }, [])

  const handlePointerUp = useCallback(() => {
    dragRef.current = null
  }, [])

  if (!isOpen) return null

  const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-2xl',
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label={title}>
      {/* Backdrop */}
      <div
        className={transparent ? 'absolute inset-0' : 'absolute inset-0 bg-black/60 backdrop-blur-sm'}
        onClick={onClose}
      />

      {/* Modal Content */}
      <div
        ref={modalRef}
        className={`relative w-full ${sizeClasses[size]} modal-content max-h-[90vh] flex flex-col ${transparent ? 'shadow-2xl' : ''}`}
        style={transparent ? { transform: `translate(${offset.x}px, ${offset.y}px)` } : undefined}
        onPointerMove={transparent ? handlePointerMove : undefined}
        onPointerUp={transparent ? handlePointerUp : undefined}
      >
        {/* Header */}
        <div
          className={`flex items-center justify-between px-6 py-4 border-b border-[var(--border)] ${transparent ? 'cursor-grab active:cursor-grabbing select-none' : ''}`}
          data-drag-handle
          onPointerDown={transparent ? handlePointerDown : undefined}
        >
          <h2 className="text-lg font-semibold text-[var(--text-primary)]">{title}</h2>
          <button
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-[var(--bg-card)] transition-colors cursor-pointer"
            aria-label="Close modal"
          >
            <svg
              className="w-5 h-5 text-[var(--text-secondary)]"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div className="px-6 py-4 border-t border-[var(--border)] flex justify-end gap-3">
            {footer}
          </div>
        )}
      </div>
    </div>
  )
}
